import React, { useState } from 'react';
import { Button, Icon, Text } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import { useTranslation } from 'Components/Helpers/translate';
import { useSoftDeployAccess } from 'App/components/hooks';
import { useSelf } from 'App/components/hooks/appHooks';
import logError from 'Helpers/logError';
import { useFloatingChatAccess } from 'App/pages/FloatingAIChat/useFloatingChatAccess';
import { useOpenContextChat } from 'App/pages/FloatingAIChat/useOpenContextChat';
import type { IntelligenceLink } from './MeetingDebriefCommon';

import './AskAboutMeetingButton.scss';

// FEA-139: the appointment modal's entry points into the floating chat. MeetingDebriefView is
// also drawn on the timeline, so the modal passes these in and the timeline gets none of them.

export const coachMarkKey = (userId: number) => `askAboutMeeting.coachMarkSeen.${userId}`;

const coachMarkSeen = (userId: number) => {
	try {
		return localStorage.getItem(coachMarkKey(userId)) === '1';
	} catch (err) {
		// Storage can be blocked; showing the coach mark again is the lesser harm.
		logError(err, 'AskAboutMeetingButton: could not read the coach mark state');
		return false;
	}
};

const markCoachMarkSeen = (userId: number) => {
	try {
		localStorage.setItem(coachMarkKey(userId), '1');
	} catch (err) {
		logError(err, 'AskAboutMeetingButton: could not save the coach mark state');
	}
};

const useCanAsk = () => {
	const hasFlag = useSoftDeployAccess('AI_ASK_ABOUT_MEETING');
	const hasChat = useFloatingChatAccess();
	return hasFlag && hasChat;
};

type Props = { appointmentId: number; intelligenceLink: IntelligenceLink };

// At the end of the summary card's header, with a one-time coach mark under it. It takes the help
// link's place, shrinking it to an icon; without the flag the link stays as it was.
export default function AskAboutMeetingButton({ appointmentId, intelligenceLink }: Props) {
	const { t } = useTranslation();
	const canAsk = useCanAsk();
	// Always set once the app has booted, which the modal cannot open before.
	const userId = useSelf()!.id;
	// Try leaves its prompt in the composer: a click on a coach mark is not a request to spend credits.
	const { open, opening } = useOpenContextChat('appointment', appointmentId, { draft: true });
	const [showCoachMark, setShowCoachMark] = useState(() => !coachMarkSeen(userId));

	if (!canAsk) {
		return <>{intelligenceLink(false)}</>;
	}

	const classes = new BemClass('AskAboutMeetingButton');

	const dismissCoachMark = () => {
		setShowCoachMark(false);
		markCoachMarkSeen(userId);
	};

	return (
		<div className={classes.b()}>
			{intelligenceLink(true)}
			<Button
				color="super-light-purple"
				shadow="none"
				size="sm"
				className={classes.elem('pill').b()}
				loading={opening}
				onClick={() => {
					dismissCoachMark();
					open();
				}}
			>
				<Icon name="stars" spacing="mr2" />
				{t('meetingDebrief.ask.button')}
			</Button>
			{showCoachMark ? (
				<div
					className={classes.elem('coachMark').b()}
					role="dialog"
					aria-label={t('meetingDebrief.ask.coachTitle')}
				>
					<Text bold color="beta-600">
						{t('meetingDebrief.ask.coachTitle')}
					</Text>
					<Text size="sm" color="grey-11" className={classes.elem('coachBody').b()}>
						{t('meetingDebrief.ask.coachBody')}
					</Text>
					<div className={classes.elem('coachActions').b()}>
						<Button
							color="purple"
							shadow="none"
							size="sm"
							onClick={() => {
								dismissCoachMark();
								open(t('meetingDebrief.ask.followUpPrompt'));
							}}
						>
							{t('meetingDebrief.ask.coachTry')}
						</Button>
						<Button type="link" color="purple" size="sm" onClick={dismissCoachMark}>
							{t('meetingDebrief.ask.coachDismiss')}
						</Button>
					</div>
				</div>
			) : null}
		</div>
	);
}
